import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import ScrollToTopButton from '../../../../components/global/ScrollToTopButton';
import WebGalleryScrollLoaderWithUpsellContainer from '../../containers/WebGalleryScrollLoaderWithUpsellContainer';
import WebGalleryUpsellModalContainer from '../../containers/WebGalleryUpsellModalContainer';
import DismissiblePersistent from '../../../../components/global/DismissiblePersistent';
import { WG_UPSELL_MODAL } from '../../../../constants/dismissibleKeys';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import WebGalleryUpsellVisibilityContainer from '../../containers/WebGalleryUpsellVisibilityContainer';
import PhotoGrid from '../../../../components/grid/PhotoGrid';
import WebGalleryPhotoCard from '../WebGalleryPhotoCard';
import WebGalleryAlbumNullState from '../WebGalleryAlbumNullState';
import WebGalleryPhotoViewerActions from '../WebGalleryPhotoViewerActions';
import { allowsFreeDownloads } from '../../../../util/album';
import ProgressivePhoto from '../../../../components/grid/ProgressivePhoto';
import WebGalleryDownloadModal from '../WebGalleryDownloadModal';
import WebGalleryFavoriteDownloadTipModal from '../WebGalleryFavoriteDownloadTipModal';
import PhotoGridViewerFooter from '../../../pro/views/Album/AlbumPhotos/components/PhotoGridViewerFooter';
import { PurchasingOptionsModal } from '../../../pro/views/HierarchyItem/Matches/components/PurchasingOptions';
import { ALBUM_PRIVACY } from '../../../../constants/albums';

const propTypes = {
  loading: PropTypes.bool,
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    is_web_gallery: PropTypes.bool,
    web_gallery_price_per_photo: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.string,
    }),
    download_without_purchase: PropTypes.bool,
    creator: PropTypes.shape({
      slug: PropTypes.string,
    }),
    root_album_folder: PropTypes.shape({
      id: PropTypes.string,
      join_code: PropTypes.string,
    }),
    display_public_photo_attribution: PropTypes.bool.isRequired,
  }),
  photoGroup: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  photos: PropTypes.shape({
    total: PropTypes.number,
    count: PropTypes.number,
    records: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        thumbnail_urls: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            width: PropTypes.number,
            height: PropTypes.number,
          }),
        ),
        watermarked_urls: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string,
            width: PropTypes.number,
            height: PropTypes.number,
          }),
        ),
        attribution: PropTypes.shape({
          name: PropTypes.string,
          username: PropTypes.string,
          avatar_photo: PropTypes.shape({
            id: PropTypes.string,
            thumbnail_urls: PropTypes.arrayOf(
              PropTypes.shape({
                url: PropTypes.string,
                width: PropTypes.number,
                height: PropTypes.number,
              }),
            ),
          }),
        }),
        is_favorite: PropTypes.bool,
      }),
    ),
  }),
  loadMore: PropTypes.func.isRequired,
  renderMeta: PropTypes.bool,
  client: PropTypes.shape({
    query: PropTypes.func.isRequired,
  }).isRequired,
  forceAllowHiResDownloads: PropTypes.bool,
};

const WebGalleryPhotoGroup = ({
  loadMore,
  photoGroup,
  album,
  photos,
  renderMeta = true,
  loading,
  forceAllowHiResDownloads,
}) => {
  if (loading || !album || !photoGroup) {
    return (
      <div className="d-block text-center mt-6">
        <span className="spinner" />
      </div>
    );
  }

  const { name } = photoGroup;
  const { records = [], total = 0 } = photos ?? {};

  if (!album.is_web_gallery) {
    return <Redirect to="/not-found" />;
  }

  if (!loading && !album && !photoGroup) {
    return <Redirect to="/not-found" />;
  }

  const canViewHiResPhoto =
    forceAllowHiResDownloads || allowsFreeDownloads(album);

  return (
    <Fragment>
      {renderMeta && (
        <Fragment>
          {records.length === 0 && <WebGalleryAlbumNullState album={album} />}
        </Fragment>
      )}

      {name &&
        !renderMeta && ( // We don't need this if META shows the name already.
          <div className="container-fluid">
            <div className="row text-center">
              <div className="col-12">
                <div className="photo-group-header">
                  <span className="photo-group-name">{name}</span>
                </div>
              </div>
            </div>
          </div>
        )}

      <div className="wg-photo-grid-listing">
        <PhotoGrid
          loading={loading}
          records={records}
          total={total}
          nativePhotoViewer
          photoViewerProps={{
            forceActionsDropdown: true,
            actions: ({ record: photo }) => (
              <WebGalleryPhotoViewerActions
                album={album}
                photo={photo}
                forceAllowHiResDownloads={forceAllowHiResDownloads}
              />
            ),
            renderPhoto: ({ photo, record }) => {
              return (
                <ProgressivePhoto
                  key={record.id}
                  id={record.id}
                  thumbnails={photo.watermarked_urls || photo.thumbnail_urls}
                  size={
                    canViewHiResPhoto
                      ? IMAGE_SIZE_CODE.LARGE1X
                      : IMAGE_SIZE_CODE.LARGE
                  }
                  allowFallbackThumbnail
                />
              );
            },
            footer: ({ record: photo }) => (
              <PhotoGridViewerFooter
                entity={album}
                photo={photo}
                isFreeWebGallery
              />
            ),
          }}
          cellRenderer={({ record: photo, recordPath }) => {
            return (
              <WebGalleryPhotoCard
                id={photo.id}
                linkTo={recordPath}
                linkProps={{
                  rel:
                    album?.privacy === ALBUM_PRIVACY.PRIVATE
                      ? 'nofollow'
                      : undefined,
                }}
                thumbnails={photo.watermarked_urls || photo.thumbnail_urls}
                progressive={false}
                size={
                  canViewHiResPhoto
                    ? IMAGE_SIZE_CODE.LARGE1X
                    : IMAGE_SIZE_CODE.LARGE
                }
              />
            );
          }}
        >
          {({ indicesRendered }) => (
            <DismissiblePersistent storeKey={WG_UPSELL_MODAL} alwaysRender>
              {(onDismiss, visible) => (
                <Fragment>
                  <WebGalleryUpsellVisibilityContainer album={album}>
                    {shouldDisplayUpsell => (
                      <WebGalleryScrollLoaderWithUpsellContainer
                        loadMore={loadMore}
                        shouldLoadMore={records.length < total}
                        disableUpsell={!shouldDisplayUpsell}
                        recordsLength={records.length}
                        indicesRendered={indicesRendered}
                        timesToLoadBeforeUpsell={1}
                      />
                    )}
                  </WebGalleryUpsellVisibilityContainer>

                  {visible && (
                    <WebGalleryUpsellModalContainer
                      album={album}
                      loading={loading}
                      onDismiss={onDismiss}
                      shouldCloseOnOverlayClick={false}
                    />
                  )}
                </Fragment>
              )}
            </DismissiblePersistent>
          )}
        </PhotoGrid>
      </div>

      <WebGalleryDownloadModal />
      <WebGalleryFavoriteDownloadTipModal />
      <PurchasingOptionsModal />
      <ScrollToTopButton duration={0} />
    </Fragment>
  );
};

WebGalleryPhotoGroup.propTypes = propTypes;

export default WebGalleryPhotoGroup;
